'use client';

import { c as _c } from "react/compiler-runtime";
import { useAuth, usePrefetchGetUser } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import { AnimatedD6 } from '@/components/market/position-card';
import { userIdentityKey } from '@/components/profile/user-identity-key';
import { DataTable } from '@/components/ui/data-table';
import { getProfileHref } from '@/lib/profile/profile-href';
import { PORTFOLIO_USER_PROFILE_FIELDS } from '@/lib/sdk/projections';
import { cn } from '@/lib/ui/utils';
import type { BackerRewardsPool } from './backer-earnings';
import { BackerEarningsCell } from './backer-earnings-cell';
import type { MarketBackerLeaderboardEntry, MarketBackerSortValue } from './types';
export type MarketBackerEntry = MarketBackerLeaderboardEntry;

/** Same identity-first card as Traders until `xl`: rank + UserIdentity, metrics under. */
const MARKET_BACKERS_MOBILE_ROW_CLASS: string = cn('max-xl:grid max-xl:grid-cols-[1.75rem_minmax(0,1fr)_minmax(0,1fr)] max-xl:items-start max-xl:gap-x-4 max-xl:gap-y-2', 'max-xl:[&>:nth-child(1)]:self-center max-xl:[&>:nth-child(2)]:col-span-2', 'max-xl:[&>:nth-child(3)]:col-start-2 max-xl:[&>:nth-child(n+3)]:mt-4');
interface MarketBackersTableProps {
  entries: readonly MarketBackerEntry[];
  rewardPool?: BackerRewardsPool | null;
  earningsUnavailable?: boolean;
  sortBy: MarketBackerSortValue;
  sortOrder: 'asc' | 'desc';
  onSortOrderChange: (order: 'asc' | 'desc') => void;
  onSortChange: (sort: MarketBackerSortValue) => void;
}
export function MarketBackersTable(t0) {
  const $ = _c(31);
  const {
    entries,
    sortBy,
    sortOrder,
    onSortOrderChange,
    onSortChange,
    rewardPool: t1,
    earningsUnavailable: t2
  } = t0;
  const rewardPool = t1 === undefined ? null : t1;
  const earningsUnavailable = t2 === undefined ? false : t2;
  const t = useTranslations("market.backers");
  const {
    user
  } = useAuth();
  const prefetchUser = usePrefetchGetUser();
  const currentUserId = user?.id ?? null;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <DataTable.RankColumn />;
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] !== t) {
    t4 = t("column_backer");
    $[1] = t;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] !== t4) {
    t5 = <DataTable.IdentityColumn header={t4} className="w-auto max-w-none" />;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== t) {
    t6 = t("column_invested");
    $[5] = t;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t6) {
    t7 = <DataTable.SortableColumn sortKey="amount" header={t6} align="right" className="w-28 shrink-0" />;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t) {
    t8 = t("column_earnings");
    $[9] = t;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t8) {
    t9 = <DataTable.SortableColumn sortKey="earnings" header={t8} align="right" className="w-28 shrink-0" />;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t5 || $[14] !== t7 || $[15] !== t9) {
    t10 = <DataTable.Header>{t3}{t5}{t7}{t9}</DataTable.Header>;
    $[13] = t5;
    $[14] = t7;
    $[15] = t9;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== currentUserId || $[18] !== earningsUnavailable || $[19] !== prefetchUser || $[20] !== rewardPool || $[21] !== t) {
    t11 = <DataTable.Body>{entry => {
        const rowKey = entry.participationId.length > 0 ? entry.participationId : userIdentityKey(entry.backer, `anonymous-rank-${entry.rank}`);
        const isUser = currentUserId !== null && entry.backer.userId === currentUserId;
        const hasProfile = entry.backer.userId !== null;
        const href = hasProfile && entry.backer.userId !== null ? getProfileHref(entry.backer.userId) : undefined;
        const prefetch = hasProfile && entry.backer.userId !== null ? () => {
          if (entry.backer.userId === null) {
            return;
          }
          prefetchUser({
            identifier: entry.backer.userId,
            fields: [...PORTFOLIO_USER_PROFILE_FIELDS]
          });
        } : undefined;
        return <DataTable.Row key={rowKey} className={MARKET_BACKERS_MOBILE_ROW_CLASS} isCurrentUser={isUser} ariaLabel={t("row_aria_label", {
          name: entry.backer.displayName ?? entry.backer.handle ?? "",
          rank: entry.rank,
          amount: entry.totalBackedD6
        })} {...href !== undefined ? {
          href
        } : {}} {...prefetch !== undefined ? {
          onMouseEnter: prefetch,
          onFocus: prefetch,
          onPointerDown: prefetch
        } : {}} data-testid="market-backers-row"><DataTable.RankCell value={entry.rank} isCurrentUser={isUser} /><DataTable.IdentityCell user={entry.backer} showYouBadge={isUser} /><DataTable.TextCell align="right" mobileLabel={t("column_invested")} className="max-xl:w-full"><span className="text-data-profit"><AnimatedD6 d6={entry.totalBackedD6} /></span></DataTable.TextCell><BackerEarningsCell sharesD6={entry.shareBalanceD6} claimedD6={entry.totalClaimedD6} snapshotD6={entry.earningsD6 ?? null} pool={rewardPool} unavailable={earningsUnavailable} /></DataTable.Row>;
      }}</DataTable.Body>;
    $[17] = currentUserId;
    $[18] = earningsUnavailable;
    $[19] = prefetchUser;
    $[20] = rewardPool;
    $[21] = t;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== entries || $[24] !== onSortChange || $[25] !== onSortOrderChange || $[26] !== sortBy || $[27] !== sortOrder || $[28] !== t10 || $[29] !== t11) {
    t12 = <DataTable entries={entries} sortBy={sortBy} sortOrder={sortOrder} onSortChange={onSortChange} onSortOrderChange={onSortOrderChange} panel="borderless" cardUntil="xl">{t10}{t11}</DataTable>;
    $[23] = entries;
    $[24] = onSortChange;
    $[25] = onSortOrderChange;
    $[26] = sortBy;
    $[27] = sortOrder;
    $[28] = t10;
    $[29] = t11;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  return t12;
}